import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { useEffect, useRef, useState } from 'react'
import type { CropRect } from '../../../graphicPageTypes'

/**
 * Real drag-to-pan + zoom-to-cover visual cropper — ports LiangCollection's
 * `content_editor.js` `createCropSession` (lines 1526-1617) 1:1: same
 * library (Cropper.js 1.6.x), same "crop box fills the whole viewport,
 * user pans/zooms the image underneath" interaction (not the usual
 * "drag the crop box" UX), same cover-fill-on-init + edge-clamp algorithm
 * so the image can never be zoomed out to reveal empty space.
 *
 * Produces the exact same persisted contract as the previous numeric-only
 * editor (`crop_x/y/width/height` as % of source, plus `focal_x/y`) so no
 * backend/renderer change is needed — `GraphicTreeRenderer.tsx`'s
 * `cropStyle` helper is unaffected.
 *
 * Replaces the earlier round's "scope decision" to skip Cropper.js
 * (SOURCE_AUDIT_V2.md §5 "Crop / focal point" row).
 */
export default function VisualCropEditor({
  label,
  imageUrl,
  value,
  defaultValue,
  aspectRatio,
  onChange,
}: {
  label: string
  imageUrl: string | null
  value: CropRect | null
  defaultValue: CropRect
  /** width/height ratio, e.g. 16/9 or 3/4 */
  aspectRatio: number
  onChange: (next: CropRect | null) => void
}) {
  const v = value ?? defaultValue
  const imgRef = useRef<HTMLImageElement>(null)
  const cropperRef = useRef<Cropper | null>(null)
  const [ready, setReady] = useState(false)
  const [zoomPct, setZoomPct] = useState(100)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    setReady(false)
    if (!imgRef.current || !imageUrl) return
    let disposed = false
    const img = imgRef.current

    const clamp = () => {
      const cropper = cropperRef.current
      if (!cropper) return
      const canvas = cropper.getCanvasData()
      const box = cropper.getCropBoxData()
      if (!canvas.width || !canvas.height || !box.width || !box.height) return
      let needScale = 1
      if (canvas.width < box.width) needScale = Math.max(needScale, box.width / canvas.width)
      if (canvas.height < box.height) needScale = Math.max(needScale, box.height / canvas.height)
      if (needScale > 1.0001) {
        const imageData = cropper.getImageData()
        if (imageData.naturalWidth) cropper.zoomTo((imageData.width / imageData.naturalWidth) * needScale)
      }
      const canvas2 = cropper.getCanvasData()
      let left = canvas2.left
      let top = canvas2.top
      const minLeft = box.left + box.width - canvas2.width
      const maxLeft = box.left
      const minTop = box.top + box.height - canvas2.height
      const maxTop = box.top
      if (left < minLeft) left = minLeft
      if (left > maxLeft) left = maxLeft
      if (top < minTop) top = minTop
      if (top > maxTop) top = maxTop
      if (Math.abs(left - canvas2.left) > 0.1 || Math.abs(top - canvas2.top) > 0.1) {
        cropper.setCanvasData({ left, top, width: canvas2.width, height: canvas2.height })
      }
    }
    const coverContainer = () => {
      const cropper = cropperRef.current
      if (!cropper) return
      const container = cropper.getContainerData()
      const imageData = cropper.getImageData()
      if (!container.width || !container.height || !imageData.naturalWidth || !imageData.naturalHeight) return
      const needZoom = Math.max(container.width / imageData.naturalWidth, container.height / imageData.naturalHeight)
      cropper.zoomTo(needZoom)
    }
    const fillCropBox = () => {
      const cropper = cropperRef.current
      if (!cropper) return
      const container = cropper.getContainerData()
      cropper.setCropBoxData({ left: 0, top: 0, width: container.width, height: container.height })
    }
    const center = () => {
      const cropper = cropperRef.current
      if (!cropper) return
      const container = cropper.getContainerData()
      const box = cropper.getCropBoxData()
      cropper.setCropBoxData({ left: (container.width - box.width) / 2, top: (container.height - box.height) / 2, width: box.width, height: box.height })
    }

    const cropper = new Cropper(img, {
      aspectRatio,
      viewMode: 0,
      dragMode: 'move',
      autoCropArea: 1,
      background: false,
      guides: false,
      center: false,
      highlight: false,
      cropBoxMovable: false,
      cropBoxResizable: false,
      toggleDragModeOnDblclick: false,
      ready: () => {
        if (disposed) return
        setTimeout(() => {
          coverContainer()
          fillCropBox()
          center()
          // apply persisted crop as an initial canvas position, if present
          applyPersistedCrop()
          clamp()
          setReady(true)
        }, 0)
      },
      zoom: () => setTimeout(clamp, 0),
      crop: () => clamp(),
    })
    cropperRef.current = cropper

    function applyPersistedCrop() {
      const c = cropperRef.current
      if (!c) return
      const imageData = c.getImageData()
      const w = imageData.naturalWidth
      const h = imageData.naturalHeight
      if (!w || !h) return
      if (v.crop_width >= 99.5 && v.crop_height >= 99.5 - 0.01 && v.crop_x < 0.5 && v.crop_y < 0.5) return
      const container = c.getContainerData()
      const cropWpx = (v.crop_width / 100) * w
      const cropHpx = (v.crop_height / 100) * h
      if (!cropWpx || !cropHpx) return
      const scale = Math.max(container.width / cropWpx, container.height / cropHpx)
      c.zoomTo(scale)
      const canvas = c.getCanvasData()
      const box = c.getCropBoxData()
      const left = box.left - (v.crop_x / 100) * w * scale
      const top = box.top - (v.crop_y / 100) * h * scale
      c.setCanvasData({ left, top, width: canvas.width, height: canvas.height })
    }

    return () => {
      disposed = true
      cropper.destroy()
      cropperRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, aspectRatio])

  const commit = () => {
    const cropper = cropperRef.current
    if (!cropper) return
    const data = cropper.getData(true)
    const imageData = cropper.getImageData()
    const w = imageData.naturalWidth
    const h = imageData.naturalHeight
    if (!w || !h) return
    onChangeRef.current({
      crop_x: Math.max(0, Math.round((data.x / w) * 1000) / 10),
      crop_y: Math.max(0, Math.round((data.y / h) * 1000) / 10),
      crop_width: Math.min(100, Math.round((data.width / w) * 1000) / 10),
      crop_height: Math.min(100, Math.round((data.height / h) * 1000) / 10),
      focal_x: v.focal_x ?? 50,
      focal_y: v.focal_y ?? 50,
    })
  }

  const zoomBy = (delta: number) => {
    cropperRef.current?.zoom(delta)
    const cropper = cropperRef.current
    if (cropper) {
      const img2 = cropper.getImageData()
      if (img2.naturalWidth) setZoomPct(Math.round((img2.width / img2.naturalWidth) * 100))
    }
  }

  if (!imageUrl) {
    return (
      <div className="ge-crop-editor ge-crop-editor-empty">
        <div className="ge-field-label">{label}</div>
        <p style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>请先选择图片</p>
      </div>
    )
  }

  return (
    <div className="ge-crop-editor">
      <div className="ge-field-label" style={{ marginBottom: 6 }}>{label}</div>
      <div className="ge-cropper-wrap" style={{ aspectRatio: `${aspectRatio}` }}>
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img ref={imgRef} src={imageUrl} className="ge-cropper-image" />
      </div>
      <div className="ge-crop-zoom-controls">
        <button type="button" className="ge-btn ge-btn-icon" onClick={() => zoomBy(-0.1)} aria-label="缩小">−</button>
        <span className="ge-crop-zoom-label">{zoomPct}%</span>
        <button type="button" className="ge-btn ge-btn-icon" onClick={() => zoomBy(0.1)} aria-label="放大">+</button>
        <button type="button" className="ge-btn" disabled={!ready} onClick={commit} style={{ marginLeft: 'auto' }}>保存此构图</button>
        <button type="button" className="ge-btn" onClick={() => onChangeRef.current(null)}>恢复默认</button>
      </div>
    </div>
  )
}
